import { Box, Button, SxProps, Theme, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import { useClampedText } from '../../../hooks/useClampedText';
import { DescriptionSourceEnum } from '../../../types/shell';
import { isDescriptionHtml, normalizeLineBreaks, sanitizeRichText } from '../../../util/richText';

interface IProps {
  description?: string;
  source: DescriptionSourceEnum;
  clamp: boolean;
}

const baseTextSx = { overflowWrap: 'anywhere' };

// MUI's baseline strips list markers and bold/italic weights.
const richTextSx = {
  '& strong, & b, & em, & i, & u, & p, & ol, & ul, & li, & h1, & h2, & h3, & h4, & h5, & h6, & blockquote': {
    all: 'revert',
  },
  '& li[style*="text-align: center"], & li[style*="text-align: right"]': { listStylePosition: 'inside' },
  '& a': { color: 'primary.main' },
  '& img': { maxWidth: '100%', height: 'auto' },
  '& > :first-of-type': { mt: 0 },
  '& > :last-child': { mb: 0 },
};

// Without this, plain-text line breaks collapse to spaces.
const plainTextSx = { whiteSpace: 'pre-wrap' };

const columnSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.5 };

const clampSx = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const toggleButtonSx: SxProps<Theme> = { alignSelf: 'flex-start', p: 0, minWidth: 'auto' };

export const DrawerDescription: React.FC<IProps> = ({ description, source, clamp }) => {
  const text = description ?? '';
  const isHtml = isDescriptionHtml(text, source);
  const content = useMemo(() => (isHtml ? sanitizeRichText(text) : normalizeLineBreaks(text)), [isHtml, text]);

  const { ref, isOverflowing, expanded, toggleExpanded } = useClampedText(clamp, content);
  const isClamped = clamp && !expanded;
  const isToggleShown = clamp && isOverflowing;

  const textSx = useMemo(
    () => ({ ...baseTextSx, ...richTextSx, ...(isClamped && clampSx), ...(!isHtml && plainTextSx) }),
    [isClamped, isHtml],
  );

  return (
    <Box sx={columnSx}>
      <Typography
        ref={ref as React.RefObject<HTMLDivElement>}
        component="div"
        variant="body1"
        color="text.primary"
        sx={textSx}
        {...(isHtml ? { dangerouslySetInnerHTML: { __html: content } } : { children: content })}
      />
      {isToggleShown && (
        <Button variant="text" color="primary" onClick={toggleExpanded} sx={toggleButtonSx}>
          {expanded ? 'Show less' : 'Read more'}
        </Button>
      )}
    </Box>
  );
};
